/**
 * The one shape for a full-width "action row" — a `<li><button></button></li>`
 * pair with a leading `aria-hidden` indicator span and a label span, the
 * destructive rows stably partitioned last under a thin divider.
 *
 * Before this module existed, `bottom-sheet.js`'s `kind: 'action'` renderer
 * and `rowActions.js`'s `≥ lg` overflow menu each built this shape by hand.
 * They drifted: the menu omitted the indicator span (so a row had one child
 * where the sheet always had two) and pinned raw `rose-*` Tailwind classes
 * where the rest of the destructive-action vocabulary already reads
 * `text-danger dark:text-danger-400`. Both surfaces now build the row here,
 * so the two can no longer disagree about what a row is.
 */

/** Exported so a test can assert the destructive label without re-pinning the raw class. */
export const ACTION_ROW_DESTRUCTIVE_LABEL_CLASS = 'text-danger dark:text-danger-400';

/**
 * True when `el` is a row `buildActionRow` marked destructive. The two sheet
 * openers (`bottom-sheet.js`, `picker-sheet.js`) call this from their
 * "focus the first focusable element on open" routine and skip a match —
 * marked here once, so both surfaces agree without either one re-deriving
 * "destructive" from a data-role string a caller is free to override.
 *
 * Rows are already stably partitioned destructive-last (`partitionDestructive`),
 * so this only changes behaviour when a row set is destructive-only: a lone
 * "Remove" row no longer receives the sheet's opening focus, where an
 * Enter pressed in the instant it appeared would otherwise fire it.
 * @param {Element|null|undefined} el
 * @returns {boolean}
 */
export function skipsInitialFocus(el) {
  return Boolean(el && el.dataset && el.dataset.actionRowDestructive === 'true');
}

/**
 * True when `el` carries the row-level disabled treatment `buildActionRow`
 * applies. Both sheet openers' "focus the first focusable element on open"
 * routine check this alongside `skipsInitialFocus`, because a disabled row
 * is aria-disabled, not natively disabled — see `buildActionRow` for why —
 * so `el.disabled` alone no longer catches it.
 * @param {Element|null|undefined} el
 * @returns {boolean}
 */
export function isActionRowDisabled(el) {
  return Boolean(el && el.getAttribute && el.getAttribute('aria-disabled') === 'true');
}

const ACTION_ROW_DIVIDER_CLASS = 'border-t border-danger/40 dark:border-danger-400/40 mt-1 pt-1';
const ACTION_ROW_BUTTON_CLASS = 'flex w-full items-center gap-3 min-h-touch py-2 text-sm text-left';

/**
 * Stable partition: every non-destructive row keeps its relative order first,
 * every destructive row keeps its relative order last.
 * @template {{ destructive?: boolean }} T
 * @param {Array<T>} rows
 * @returns {Array<T>}
 */
export function partitionDestructive(rows) {
  const nonDestructive = rows.filter((r) => !r.destructive);
  const destructive = rows.filter((r) => r.destructive);
  return nonDestructive.concat(destructive);
}

/**
 * @param {{
 *   label: string,
 *   id?: string,
 *   destructive?: boolean,
 *   isFirstDestructive?: boolean,
 *   disabled?: boolean,
 *   dataRole?: string,
 *   defaultDataRole: string,
 *   icon?: string|HTMLElement,
 * }} opts
 * @returns {{ li: HTMLLIElement, btn: HTMLButtonElement, indicator: HTMLSpanElement, labelEl: HTMLSpanElement }}
 */
export function buildActionRow({
  label,
  id,
  destructive = false,
  isFirstDestructive = false,
  disabled = false,
  dataRole,
  defaultDataRole,
  icon,
}) {
  const li = document.createElement('li');
  if (isFirstDestructive) {
    li.className = ACTION_ROW_DIVIDER_CLASS;
  }

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = ACTION_ROW_BUTTON_CLASS;
  btn.dataset.role = dataRole || defaultDataRole;
  // The inline primary row's `button()` already stamps `data-action-id` —
  // this was the one place an ActionDescriptor's `id` was dropped on the
  // floor, so a test could only compare the two overflow surfaces by label.
  if (id) btn.dataset.actionId = id;
  if (disabled) {
    // aria-disabled, never the native attribute: a native `disabled` button
    // can never receive focus, which is fatal for the ≥ lg menu's roving-
    // tabindex pattern (the roving target has to land somewhere) — and
    // until now was the one dimension the sheet (native attribute) and the
    // menu (aria-disabled, applied by hand in rowActions.js) expressed
    // differently for the same descriptor.
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('opacity-50', 'cursor-not-allowed');
  }

  const indicator = document.createElement('span');
  indicator.setAttribute('aria-hidden', 'true');
  if (icon && typeof icon !== 'string') {
    indicator.append(icon);
  } else if (icon) {
    indicator.textContent = icon;
  }

  const labelEl = document.createElement('span');
  labelEl.textContent = label;
  if (destructive) {
    labelEl.className = ACTION_ROW_DESTRUCTIVE_LABEL_CLASS;
    btn.dataset.actionRowDestructive = 'true';
  }

  btn.append(indicator, labelEl);
  li.append(btn);

  return { li, btn, indicator, labelEl };
}
